<template>
  <div class='advisor-core-docmaster '>
    <div>
      <section class="section">
        <p class="title has-text-centered is-1 ">
          Let's Get Organized
        </p>
        <p class="subtitle has-text-centered ">
          We know it's tough to stay on top of your financial life, and we're here to help. Your personalized YourStake
          AI assistant can help walk you through finding and uploading your documents for analysis.
        </p>

      </section>


      <section class="section">
        <!-- css grid. do i really want a grid? or do i just want columns? i can do a nice border with a grid though. let's give it a shot -->
         <div id="docmaster">
          <checklist>
            <p class="title">Your Financial Life Checklist</p>
          </checklist>
          <ai-assistant>
            <figure class="image is-128x128">
              <img src="https://bulma.io/assets/images/placeholders/256x256.png" />
            </figure>
          </ai-assistant>
          <chat>
            chat goes here
          </chat>
         </div>

      </section>
      <section class="section">
        <div class="container">
          <div class="columns">
            <div class="column is-three-quarters">
              <div class="box">
                <h3 class="title is-3 ">Your Financial Life Checklist </h3>
                <hr>
                <!-- Upload button -->
                <div class="file is-medium has-name is-boxed mb-5">
                  <label class="file-label">
                    <input class="file-input" type="file" name="documents" multiple @change="handleFileUpload" />
                    <span class="file-cta">
                      <span class="file-icon">
                        <i class="fas fa-upload"></i>
                      </span>
                      <span class="file-label">Upload your documents…</span>
                    </span>
                  </label>
                </div>
                <!-- Display uploaded file names -->
                <div v-if="fileNames.length">
                  <h4 class="title is-5">Uploaded Files:</h4>
                  <ul>
                    <li v-for="(file, index) in fileNames" :key="index">{{ file }}</li>
                  </ul>
                </div>
              </div>
            </div>
            <!-- Document Checklist -->
            <div class="column is-one-quarter">
              <div class="box">
                <h3 class="title is-4">Document Checklist</h3>
                <hr>
                <div v-for="(item, index) in checklist" :key="index">
                  <label class="checkbox">
                    <input type="checkbox" :checked="item.checked" disabled />
                    <a href="#" @click.prevent="showHelpModal(index)">{{ item.name }}</a>
                    <ul v-if="item.files.length">
                      <li v-for="(file, fileIndex) in item.files" :key="fileIndex">{{ file }}</li>
                    </ul>
                  </label>
                  <br>
                </div>
              </div>
            </div>
          </div>
          <div v-if="notification.visible" class="notification is-link">
            Great job uploading {{ notification.uploadedDocs.join(', ') }}. It looks like you're missing {{
            notification.missingDocs.join(', ') }}. Please <a href="#" @click="startTour">click here</a> for help on
            where to find these documents.
          </div>
        </div>
      </section>
      <!-- Help Modals -->
      <div v-for="(item, index) in checklist" :key="'modal-' + index" v-if="item.showHelpModal" class="modal is-active">
        <div class="modal-background"></div>
        <div class="modal-content">
          <div class="box">
            <h4 class="title">{{ item.name }}</h4>
            <p>Here's how you can find and upload {{ item.name }}:</p>
            <img :src="getHelpImage(index)" :alt="'Help image for ' + item.name">
            <p>For example, you can get your {{ item.name.toLowerCase() }} by following these steps:</p>
            <button class="button is-link" @click="closeHelpModal(index)">Close</button>
          </div>
        </div>
        <button class="modal-close is-large" aria-label="close" @click="closeHelpModal(index)"></button>
      </div>
      <!-- Tour Steps -->
      <div id="tourStep1" class="tour-step">
        <p>Welcome to the Document Upload Page! Let's get started by understanding the documents you need to upload.</p>
        <button @click="nextStep(1)">Next</button>
      </div>
      <div id="tourStep2" class="tour-step">
        <p>Click here to upload your documents. Make sure to select the correct files as requested.</p>
        <img src="path/to/sample-upload-button.png" alt="Upload button example" />
        <button @click="nextStep(2)">Next</button>
      </div>
      <div id="tourStep3" class="tour-step">
        <p>This section will notify you if any documents are missing. Click the link for help on where to find these
          documents.</p>
        <button @click="nextStep(3)">Next</button>
      </div>
      <div id="tourStep4" class="tour-step">
        <p>Check this area for specific document information and upload status.</p>
        <img src="path/to/sample-document-checklist.png" alt="Document checklist example" />
        <button @click="nextStep(4)">Next</button>
      </div>
      <div id="tourStep5" class="tour-step">
        <p>For example, you can get your bank statement by logging onto your bank account, clicking "statements", and
          downloading the most recent statement.</p>
        <img src="path/to/sample-bank-statement.png" alt="Bank statement example" />
        <button @click="nextStep(5)">Next</button>
      </div>
      <div id="tourStep6" class="tour-step">
        <p>Here is an example of an insurance statement. Make sure to upload similar documents.</p>
        <img src="path/to/sample-insurance-statement.png" alt="Insurance statement example" />
        <button @click="nextStep(6)">Next</button>
      </div>
      <div id="tourStep7" class="tour-step">
        <p>Here is an example of a 1040 Tax Return. You can find this document in your tax filing records or request it
          from your tax preparer.</p>
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/64/Form_1040%2C_2011.pdf/page1-926px-Form_1040%2C_2011.pdf.jpg?20120217104732"
          alt="1040 Tax Return example" />
        <button @click="endTour">Done</button>
      </div>
      <div id="tourOverlay" class="tour-overlay"></div>

      <!-- Editable Bank Statement Information -->
      <div v-if="bankStatementInfo.length" class="box">
        <h3 class="title is-5 mt-4">Bank Statement Information:</h3>
        <div v-for="(info, index) in bankStatementInfo" :key="index" class="box">
          <strong>{{ info.type }}</strong>
          <div class="field">
            <label class="label">Name</label>
            <div class="control">
              <input class="input" type="text" v-model="info.name" />
            </div>
          </div>
          <div class="field">
            <label class="label">Balance</label>
            <div class="control">
              <input class="input" type="text" v-model="info.balance" />
            </div>
          </div>
        </div>
      </div>

      <!-- Editable Investment Information -->
      <div v-if="investmentInfo.length" class="box">
        <h3 class="title is-5">Investment Information:</h3>
        <div v-for="(info, index) in investmentInfo" :key="index" class="box">
          <strong>{{ info.type }}</strong>
          <div class="field">
            <label class="label">Custodian</label>
            <div class="control">
              <input class="input" type="text" v-model="info.custodian" />
            </div>
          </div>
          <div class="field">
            <label class="label">Account Name</label>
            <div class="control">
              <input class="input" type="text" v-model="info.account_name" />
            </div>
          </div>
          <div class="field">
            <label class="label">Balance</label>
            <div class="control">
              <input class="input" type="number" v-model="info.balance" />
            </div>
          </div>
        </div>
      </div>

      <!-- Editable Tax Return Information -->
      <div v-if="taxReturnInfo.length" class="box">
        <h3 class="title is-5">Tax Return Information:</h3>
        <div v-for="(info, index) in taxReturnInfo" :key="index" class="box">
          <strong>{{ info.type }}</strong>
          <div class="field">
            <label class="label">Taxpayer Name</label>
            <div class="control">
              <input class="input" type="text" v-model="info.taxpayer_name" />
            </div>
          </div>
          <div class="field">
            <label class="label">Filing Status</label>
            <div class="control">
              <input class="input" type="text" v-model="info.filing_status" />
            </div>
          </div>
          <div class="field">
            <label class="label">Total Income</label>
            <div class="control">
              <input class="input" type="number" v-model="info.total_income" />
            </div>
          </div>
          <div class="field">
            <label class="label">Tax Owed</label>
            <div class="control">
              <input class="input" type="number" v-model="info.tax_owed" />
            </div>
          </div>
        </div>
      </div>

      <div class="box"
        v-if="!showGoalsForm && !showGeneratedPlan && (investmentInfo.length || bankStatementInfo.length)">
        <button class="button is-primary" @click="showGoalsForm = true">Create Financial Plan</button>
      </div>
    </div>
    </section>

    <!-- Display Generated Financial Plan -->
    <FinancialPlan v-if="showGoalsForm" :showEstateInfo="showEstateInfo" :estateInfo="estateInfo"
      :investmentInfo="investmentInfo" :bankStatementInfo="bankStatementInfo" :taxReturnInfo="taxReturnInfo" />
  </div>

</template>

<script>
import FinancialPlan from '../components/FinancialPlan.vue';
import AssetBreakdownChart from '../components/DocmasterAssetAllocation.vue';
import RetirementProjectionChart from '../components/DocmasterRetirementProjectionChart.vue';

export default {
  name: 'AdvisorCoreDocMaster',
  
  components: { 
    FinancialPlan,
    AssetBreakdownChart,
    RetirementProjectionChart
  },
  data() {
    return {
      showGoalsForm: false,
      showGeneratedPlan: false,
      showEstateInfo: false,
      fileNames: [],
      checklist: [
        { name: "Bank Statements", checked: false, files: [], showHelpModal: false },
        { name: "Investment Statements", checked: false, files: [], showHelpModal: false },
        { name: "Insurance Statements", checked: false, files: [], showHelpModal: false },
        { name: "1040 Tax Return", checked: false, files: [], showHelpModal: false },
        { name: "Social Security Benefit Estimate Statement", checked: false, files: [], showHelpModal: false },
        { name: "Health Proxy", checked: false, files: [], showHelpModal: false },
        { name: "Will", checked: false, files: [], showHelpModal: false },
        { name: "Power of Attorney", checked: false, files: [], showHelpModal: false }
      ],
      notification: {
        visible: false,
        uploadedDocs: [],
        missingDocs: []
      },
      bankStatementInfo: [],
      investmentInfo: [],
      taxReturnInfo: [],
      estateInfo: {
        health_proxy: {
          agent_name: "",
          agent_contact: "",
          alternate_agent_name: "",
          alternate_agent_contact: "",
          effective_date: "",
          instructions: ""
        },
        will: {
          testator_name: "",
          executor_name: "",
          beneficiaries: "",
          assets: "",
          date_of_will: "",
          witnesses: ""
        },
        power_of_attorney: {
          principal_name: "",
          agent_name: "",
          agent_contact: "",
          powers_granted: "",
          effective_date: "",
          expiration_date: ""
        }
      },
    };
  },
  computed: {
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
      };
    },
  },
  methods: {
    startTour() {
      document.getElementById('tourStep1').style.display = 'block';
      document.getElementById('tourOverlay').style.display = 'block';
    },
    nextStep(step) {
      document.getElementById(`tourStep${step}`).style.display = 'none';
      document.getElementById(`tourStep${step + 1}`).style.display = 'block';
    },
    endTour() {
      document.getElementById('tourStep7').style.display = 'none';
      document.getElementById('tourOverlay').style.display = 'none';
    },
    async handleFileUpload(event) {
      const files = Array.from(event.target.files);
      this.fileNames = files.map(file => file.name);
      console.log('Files selected:', this.fileNames);

      for (const file of files) {
        await this.sendFileToBackend(file);
      }
      this.updateNotification();
    },
    async sendFileToBackend(file) {
      const url = '/api/v1/process/document_classification/';
      const formData = new FormData();
      formData.append('document', file);
      console.log('Sending file to backend:', file.name);

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: this.headers,
          body: formData,
        });
        const result = await res.json();
        console.log('Response from backend:', result);
        this.updateChecklist(result.classification, file.name);
        if (result.classification === "Bank Statements") {
          this.updateBankStatementInfo(result.info);
        } else if (result.classification === "Investment Statements") {
          this.updateInvestmentInfo(result.info);
        } else if (result.classification === "1040 Tax Return") {
          this.updateTaxReturnInfo(result.info);
        } else if (result.classification === "Health Proxy") {
          this.updateHealthProxyInfo(result.info);
        } else if (result.classification === "Will") {
          this.updateWillInfo(result.info);
        } else if (result.classification === "Power of Attorney") {
          this.updatePowerOfAttorneyInfo(result.info);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    },
    updateChecklist(classification, fileName) {
      console.log('Updating checklist for classification:', classification, 'with file:', fileName);
      const item = this.checklist.find(item => item.name === classification);
      if (item) {
        item.checked = true;
        item.files.push(fileName);
      }
    },
    updateNotification() {
      const uploadedDocs = this.checklist.filter(item => item.checked).map(item => item.name);
      const missingDocs = this.checklist.filter(item => !item.checked).map(item => item.name);
      this.notification.uploadedDocs = uploadedDocs;
      this.notification.missingDocs = missingDocs;
      this.notification.visible = true;
    },
    updateInvestmentInfo(info) {
      console.log('Updating investment info with:', info);
      const newAccounts = info.investment_accounts.map(account => ({
        type: "Investment Account",
        custodian: account.custodian,
        account_name: account.account_name,
        balance: account.balance
      }));
      this.investmentInfo = [...this.investmentInfo, ...newAccounts];
    },
    updateTaxReturnInfo(info) {
      console.log('Updating tax return info with:', info);
      this.taxReturnInfo = [
        { type: "1040 Tax Return", taxpayer_name: info.tax_return.taxpayer_name, filing_status: info.tax_return.filing_status, total_income: info.tax_return.total_income, tax_owed: info.tax_return.tax_owed }
      ];
    },
    updateBankStatementInfo(info) {
      console.log('Updating bank statement info with:', info);
      this.bankStatementInfo = [];

      this.bankStatementInfo.push({
        type: "Checking Accounts",
        name: info.checking.name,
        balance: info.checking.balance
      });
    },
    updateHealthProxyInfo(info) {
      console.log('Updating estate info with:', info);
      this.estateInfo.health_proxy = info;
      this.showEstateInfo = true;
    },
    updateWillInfo(info) {
      console.log('Updating estate info with:', info);
      this.estateInfo.will = info;
      this.showEstateInfo = true;
    },
    updatePowerOfAttorneyInfo(info) {
      console.log('Updating estate info with:', info);
      this.estateInfo.power_of_attorney = info;
      this.showEstateInfo = true;
    },
    showHelpModal(index) {
      console.log('Showing help modal for index:', index);
      this.$set(this.checklist[index], 'showHelpModal', true);
    },
    closeHelpModal(index) {
      this.$set(this.checklist[index], 'showHelpModal', false);
    },
    getHelpImage(index) {
      const images = [
        'path/to/sample-bank-statement.png',
        'path/to/sample-investment-statement.png',
        'path/to/sample-insurance-statement.png',
        'https://upload.wikimedia.org/wikipedia/commons/thumb/6/64/Form_1040%2C_2011.pdf/page1-926px-Form_1040%2C_2011.pdf.jpg?20120217104732',
        'path/to/sample-social-security-statement.png',
        'path/to/sample-health-proxy.png',
        'path/to/sample-will.png',
        'path/to/sample-power-of-attorney.png'
      ];
      return images[index];
    },
  }
};
</script>
<style scoped>
.majestic-gradient {
  background: linear-gradient(135deg, #6F5CC2 0%, #D5A9E8 100%);
  min-height: 100vh;
  padding: 20px;
  font-family: 'Arial', sans-serif;
}

.box {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.title, .subtitle {
  color: #4a4a4a;
}

.label {
  margin-top: 10px;
  font-weight: bold;
}

.input, .file-input {
  border-radius: 5px;
  border: 1px solid #d5d5d5;
}

.checkbox {
  margin-top: 10px;
}

.report-section {
  padding: 20px;
}

h3.title, h4.title {
  margin-bottom: 15px;
  color: #333;
}

h5.title {
  margin-bottom: 10px;
  color: #555;
}

p {
  margin-bottom: 10px;
  line-height: 1.6;
}

.canvas-container {
  max-width: 100%;
  max-height: 400px;
  margin-bottom: 20px;
}

.insights-section .insight {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.tour-step {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: none;
  max-width: 80%;
  max-height: 80%;
  overflow: auto;
}

.tour-step img {
  max-width: 100%;
  height: auto;
}

.tour-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: none;
}

#docmaster {
  display: grid;
  grid-template-columns: 3fr 1fr;
  max-width: 70em;
  margin: auto;
  gap: 2px;
  background-color: #333;
} 
#docmaster > * {
  background-color: white;
}

</style>
